import { Check, CheckCircle, X } from "@phosphor-icons/react";
import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { IconButton } from "@/components/primitives/Button";
import { WarningAlertIcon, warningAccentBackgroundStyle, warningSoftBorderStyle, warningSurfaceTextStyle, warningTextStyle } from "@/components/ui/feedback/warningVisual";
import { twRing } from "@/lib/emotionTw";

export type HorizontalStepperState = "done" | "active" | "pending" | "disabled";
export type HorizontalStepperVariant = "primary" | "success" | "info" | "warning" | "neutral";
export type HorizontalStepperSize = "sm" | "md";

export type HorizontalStepperStep = {
  id?: string;
  label: ReactNode;
  secondaryText?: ReactNode;
  /** Backwards-compatible alias for secondaryText. Prefer secondaryText for new code. */
  description?: ReactNode;
  status?: HorizontalStepperState;
  disabled?: boolean;
};

export type HorizontalStepperProps = HTMLAttributes<HTMLElement> & {
  steps: HorizontalStepperStep[];
  currentStep?: number;
  title?: ReactNode;
  subheading?: ReactNode;
  /** Backwards-compatible alias for subheading. Prefer subheading for new code. */
  description?: ReactNode;
  announcement?: ReactNode;
  closeButton?: boolean;
  closeLabel?: string;
  onClose?: () => void;
  onStepSelect?: (step: HorizontalStepperStep, index: number) => void;
  size?: HorizontalStepperSize;
  surface?: "card" | "plain";
  variant?: HorizontalStepperVariant;
};

/* Exact compiled output of the pre-migration Tailwind utilities, including
 * the sm: responsive rules (min-width:640px). Net line-heights follow the
 * old tailwind-merge/cascade resolution: leading-* utilities sit later in
 * the compiled stylesheet than the font-size utilities they accompany. */

/* Non-CSS per-size settings (icon sizes / IconButton size). */
const sizeConfig = {
  sm: { announcementIcon: 18, closeIcon: 18, closeSize: "md" as const, checkIcon: 13 },
  md: { announcementIcon: 22, closeIcon: 22, closeSize: "lg" as const, checkIcon: 16 }
} as const;

const sectionSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: {
    paddingLeft: "1.25rem",
    paddingRight: "1.25rem",
    paddingTop: "1.25rem",
    paddingBottom: "1.25rem",
    "@media (min-width:640px)": {
      paddingLeft: "1.5rem",
      paddingRight: "1.5rem"
    }
  },
  md: {
    paddingLeft: "1.75rem",
    paddingRight: "1.75rem",
    paddingTop: "1.75rem",
    paddingBottom: "1.75rem",
    "@media (min-width:640px)": {
      paddingLeft: "2.25rem",
      paddingRight: "2.25rem"
    }
  }
};

/* sm: text-xs + leading-4 → .75rem/1rem; md: text-sm + leading-5 → .875rem/1.25rem
 * (the leading-* utility wins — later in the compiled stylesheet). */
const announcementSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: {
    minHeight: "2.5rem",
    borderRadius: "var(--bfrs-radius-md)",
    paddingTop: ".5rem",
    paddingBottom: ".5rem",
    paddingLeft: ".875rem",
    paddingRight: "1rem",
    fontSize: ".75rem",
    lineHeight: "1rem"
  },
  md: {
    minHeight: "3rem",
    borderRadius: "var(--bfrs-radius-lg)",
    paddingTop: ".625rem",
    paddingBottom: ".625rem",
    paddingLeft: "1.25rem",
    paddingRight: "1.25rem",
    fontSize: ".875rem",
    lineHeight: "1.25rem"
  }
};

/* pr-12/pr-14 dropped the earlier pr-4/pr-5 via tailwind-merge. */
const announcementWithCloseSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { paddingRight: "3rem" },
  md: { paddingRight: "3.5rem" }
};

const closePositionSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { right: "1.25rem", top: "1.25rem" },
  md: { right: "2rem", top: "2rem" }
};

const stepsSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { marginTop: "1.75rem", maxWidth: "420px" },
  md: { marginTop: "2.25rem", maxWidth: "520px" }
};

/* mt-1/mt-2 dropped the earlier mt-7/mt-9 via tailwind-merge. */
const stepsWithoutAnnouncementSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { marginTop: ".25rem" },
  md: { marginTop: ".5rem" }
};

const markerSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { width: "1.75rem", height: "1.75rem" },
  md: { width: "2.25rem", height: "2.25rem" }
};

const dotSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { width: ".5rem", height: ".5rem" },
  md: { width: ".625rem", height: ".625rem" }
};

const connectorSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { left: "calc(50% + 28px)", top: ".875rem", width: "calc(100% - 56px)" },
  md: { left: "calc(50% + 34px)", top: "18px", width: "calc(100% - 68px)" }
};

const stepGapSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { gap: ".5rem" },
  md: { gap: ".625rem" }
};

const labelSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { fontSize: "13px", lineHeight: "1.25rem" },
  md: { fontSize: "15px", lineHeight: "1.5rem" }
};

/* md: text-xs + leading-5 → .75rem/1.25rem (leading-5 later in stylesheet). */
const stepDescriptionSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { fontSize: "11px", lineHeight: "1rem" },
  md: { fontSize: ".75rem", lineHeight: "1.25rem" }
};

const textBlockSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { maxWidth: "124px" },
  md: { maxWidth: "145px" }
};

const contentSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { marginTop: "1.25rem", gap: ".25rem" },
  md: { marginTop: "1.5rem", gap: ".375rem" }
};

/* mt-4/mt-5 dropped the earlier mt-5/mt-6 via tailwind-merge. */
const contentWithoutStepsSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { marginTop: "1rem" },
  md: { marginTop: "1.25rem" }
};

const titleSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: {
    fontSize: "24px",
    lineHeight: 1.25,
    "@media (min-width:640px)": { fontSize: "26px" }
  },
  md: {
    fontSize: "28px",
    lineHeight: 1.25,
    "@media (min-width:640px)": { fontSize: "32px" }
  }
};

const subheadingSize: Record<HorizontalStepperSize, CSSObject> = {
  sm: { fontSize: "15px", lineHeight: "1.5rem" },
  md: { fontSize: "17px", lineHeight: "1.75rem" }
};

/* The warning variant carried empty class strings — inline styles drive it. */
const announcementVariant: Record<HorizontalStepperVariant, CSSObject> = {
  primary: {
    borderColor: "hsl(var(--bfrs-color-primary) / .4)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
  },
  success: {
    borderColor: "hsl(var(--bfrs-color-success) / .25)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-success-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-success-contrast) / var(--tw-text-opacity, 1))"
  },
  info: {
    borderColor: "hsl(var(--bfrs-color-info) / .25)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-info-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-info-contrast) / var(--tw-text-opacity, 1))"
  },
  warning: {},
  neutral: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  }
};

const activeMarkerVariant: Record<HorizontalStepperVariant, CSSObject> = {
  primary: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
  },
  success: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-success) / var(--tw-border-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-success) / var(--tw-text-opacity, 1))"
  },
  info: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-info) / var(--tw-border-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-info) / var(--tw-text-opacity, 1))"
  },
  warning: {},
  neutral: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-text-secondary) / var(--tw-border-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
  }
};

const activeDotVariant: Record<HorizontalStepperVariant, CSSObject> = {
  primary: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))"
  },
  success: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-success) / var(--tw-bg-opacity, 1))"
  },
  info: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-info) / var(--tw-bg-opacity, 1))"
  },
  warning: {},
  neutral: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-text-secondary) / var(--tw-bg-opacity, 1))"
  }
};

const doneMarkerVariant: Record<HorizontalStepperVariant, CSSObject> = {
  primary: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
  },
  success: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-success) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-success) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
  },
  info: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-info) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-info) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
  },
  warning: {},
  neutral: {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-text-secondary) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-text-secondary) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
  }
};

const activeTextVariant: Record<HorizontalStepperVariant, CSSObject> = {
  primary: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
  },
  success: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-success-contrast) / var(--tw-text-opacity, 1))"
  },
  info: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-info-contrast) / var(--tw-text-opacity, 1))"
  },
  warning: {},
  neutral: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  }
};

const HorizontalStepperRoot = styled("section", {
  label: "AgenticHorizontalStepper-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize; surface: "card" | "plain" } }>(({ ownerState }) => [
  {
    position: "relative",
    width: "100%",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  sectionSize[ownerState.size],
  ownerState.surface === "card" && {
    borderRadius: "var(--bfrs-radius-xl)",
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-card)",
    "--tw-shadow-colored": "var(--bfrs-shadow-card)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  }
]);

const HorizontalStepperAnnouncement = styled("div", {
  label: "AgenticHorizontalStepper-announcement",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize; variant: HorizontalStepperVariant; canClose: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    alignItems: "center",
    borderWidth: "1px",
    fontWeight: 700
  },
  announcementSize[ownerState.size],
  announcementVariant[ownerState.variant],
  ownerState.canClose && announcementWithCloseSize[ownerState.size]
]);

const HorizontalStepperWarningIconWrap = styled("span", {
  label: "AgenticHorizontalStepper-warningIconWrap"
})({
  marginRight: ".5rem",
  display: "flex",
  flexShrink: 0
});

const HorizontalStepperAnnouncementIcon = styled(CheckCircle, {
  label: "AgenticHorizontalStepper-announcementIcon"
})({
  marginRight: ".5rem",
  flexShrink: 0
});

const HorizontalStepperAnnouncementText = styled("span", {
  label: "AgenticHorizontalStepper-announcementText"
})({
  minWidth: "0px"
});

/* `&&` overrides beat IconButton's own variant rules, reproducing the old
 * tailwind-merge resolution of the className overrides. */
const HorizontalStepperCloseButton = styled(IconButton, {
  label: "AgenticHorizontalStepper-closeButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize } }>(({ ownerState }) => ({
  "&&": {
    position: "absolute",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
    },
    ...closePositionSize[ownerState.size]
  }
}));

const HorizontalStepperList = styled("ol", {
  label: "AgenticHorizontalStepper-list",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize; hasAnnouncement: boolean } }>(({ ownerState }) => [
  {
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    width: "100%",
    alignItems: "flex-start"
  },
  stepsSize[ownerState.size],
  !ownerState.hasAnnouncement && stepsWithoutAnnouncementSize[ownerState.size]
]);

const HorizontalStepperItem = styled("li", {
  label: "AgenticHorizontalStepper-item"
})({
  position: "relative",
  display: "flex",
  minWidth: "0px",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center"
});

const HorizontalStepperConnector = styled("span", {
  label: "AgenticHorizontalStepper-connector",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize } }>(({ ownerState }) => [
  {
    position: "absolute",
    height: ".125rem",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-bg-opacity, 1))"
  },
  connectorSize[ownerState.size]
]);

const HorizontalStepperStepButton = styled("button", {
  label: "AgenticHorizontalStepper-stepButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize } }>(({ ownerState }) => [
  {
    position: "relative",
    zIndex: 10,
    display: "grid",
    justifyItems: "center",
    borderRadius: "var(--bfrs-radius-lg)",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:hover": {
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
    },
    "&:focus-visible": {
      ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
      "--tw-ring-opacity": "1",
      "--tw-ring-offset-width": "2px",
      "--tw-ring-offset-color": "hsl(var(--bfrs-color-surface-card) / 1)"
    }
  },
  stepGapSize[ownerState.size]
]);

const HorizontalStepperStepStatic = styled("div", {
  label: "AgenticHorizontalStepper-stepStatic",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize } }>(({ ownerState }) => [
  {
    position: "relative",
    zIndex: 10,
    display: "grid",
    justifyItems: "center"
  },
  stepGapSize[ownerState.size]
]);

const HorizontalStepperDoneMarker = styled("span", {
  label: "AgenticHorizontalStepper-doneMarker",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize; variant: HorizontalStepperVariant } }>(({ ownerState }) => [
  {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--bfrs-radius-pill)",
    borderWidth: "2px",
    "--tw-shadow": "var(--bfrs-shadow-low)",
    "--tw-shadow-colored": "var(--bfrs-shadow-low)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  markerSize[ownerState.size],
  doneMarkerVariant[ownerState.variant]
]);

const HorizontalStepperDotMarker = styled("span", {
  label: "AgenticHorizontalStepper-dotMarker",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize; variant: HorizontalStepperVariant; active: boolean; disabled: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--bfrs-radius-pill)",
    borderWidth: "2px",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    transitionProperty: "color,background-color,border-color,text-decoration-color,fill,stroke",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".2s"
  },
  markerSize[ownerState.size],
  ownerState.active
    ? activeMarkerVariant[ownerState.variant]
    : {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-border-opacity, 1))",
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
      },
  ownerState.disabled && { opacity: ".55" }
]);

const HorizontalStepperDot = styled("span", {
  label: "AgenticHorizontalStepper-dot",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize; variant: HorizontalStepperVariant; active: boolean } }>(({ ownerState }) => [
  {
    borderRadius: "var(--bfrs-radius-pill)"
  },
  dotSize[ownerState.size],
  ownerState.active
    ? activeDotVariant[ownerState.variant]
    : {
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-text-secondary) / var(--tw-bg-opacity, 1))"
      }
]);

const HorizontalStepperTextBlock = styled("span", {
  label: "AgenticHorizontalStepper-textBlock",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize } }>(({ ownerState }) => [
  {
    display: "grid",
    gap: ".25rem"
  },
  textBlockSize[ownerState.size]
]);

const HorizontalStepperStepLabel = styled("span", {
  label: "AgenticHorizontalStepper-stepLabel",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize; variant: HorizontalStepperVariant; muted: boolean } }>(({ ownerState }) => [
  {
    fontWeight: 700
  },
  labelSize[ownerState.size],
  ownerState.muted
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
      }
    : activeTextVariant[ownerState.variant]
]);

const HorizontalStepperStepDescription = styled("span", {
  label: "AgenticHorizontalStepper-stepDescription",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize } }>(({ ownerState }) => [
  {
    fontWeight: 400,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  stepDescriptionSize[ownerState.size]
]);

const HorizontalStepperContent = styled("div", {
  label: "AgenticHorizontalStepper-content",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize; withoutSteps: boolean } }>(({ ownerState }) => [
  {
    display: "grid"
  },
  contentSize[ownerState.size],
  ownerState.withoutSteps && contentWithoutStepsSize[ownerState.size]
]);

const HorizontalStepperTitle = styled("h3", {
  label: "AgenticHorizontalStepper-title",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize } }>(({ ownerState }) => [
  {
    fontWeight: 700,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  titleSize[ownerState.size]
]);

const HorizontalStepperSubheading = styled("p", {
  label: "AgenticHorizontalStepper-subheading",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: HorizontalStepperSize } }>(({ ownerState }) => [
  {
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
  },
  subheadingSize[ownerState.size]
]);

function getStepState(step: HorizontalStepperStep, index: number, currentStep: number): HorizontalStepperState {
  if (step.disabled) return "disabled";
  if (step.status) return step.status;
  if (index < currentStep) return "done";
  if (index === currentStep) return "active";
  return "pending";
}

function StepMarker({
  state,
  size,
  variant
}: {
  state: HorizontalStepperState;
  size: HorizontalStepperSize;
  variant: HorizontalStepperVariant;
}) {
  const isWarningVariant = variant === "warning";

  if (state === "done") {
    return (
      <HorizontalStepperDoneMarker ownerState={{ size, variant }} style={isWarningVariant ? warningSurfaceTextStyle : undefined}>
        <Check size={sizeConfig[size].checkIcon} weight="bold" />
      </HorizontalStepperDoneMarker>
    );
  }

  const isActive = state === "active";

  return (
    <HorizontalStepperDotMarker
      ownerState={{ size, variant, active: isActive, disabled: state === "disabled" }}
      style={isActive && isWarningVariant ? { ...warningSoftBorderStyle, ...warningTextStyle } : undefined}
    >
      <HorizontalStepperDot
        ownerState={{ size, variant, active: isActive }}
        style={isActive && isWarningVariant ? warningAccentBackgroundStyle : undefined}
      />
    </HorizontalStepperDotMarker>
  );
}

export const HorizontalStepper = forwardRef<HTMLElement, HorizontalStepperProps>(
  (
    {
      className,
      steps,
      currentStep = 0,
      title,
      subheading,
      description,
      announcement,
      closeButton = false,
      closeLabel = "Close",
      onClose,
      onStepSelect,
      size = "sm",
      surface = "card",
      variant = "primary",
      style,
      ...props
    },
    ref
  ) => {
    const resolvedSteps = steps.map((step, index) => ({
      ...step,
      state: getStepState(step, index, currentStep)
    }));
    const stepGridStyle = {
      gridTemplateColumns: `repeat(${Math.max(resolvedSteps.length, 1)}, minmax(0, 1fr))`
    } as CSSProperties;
    const canClose = closeButton && Boolean(onClose);
    const resolvedSize = sizeConfig[size] ? size : "sm";
    const resolvedVariant = announcementVariant[variant] ? variant : "primary";
    const resolvedSubheading = subheading ?? description;

    return (
      <HorizontalStepperRoot
        ref={ref}
        ownerState={{ size: resolvedSize, surface }}
        className={className}
        style={style}
        aria-label={typeof title === "string" ? title : "Step progress"}
        {...props}
      >
        {announcement && (
          <HorizontalStepperAnnouncement
            ownerState={{ size: resolvedSize, variant: resolvedVariant, canClose }}
            style={resolvedVariant === "warning" ? warningSurfaceTextStyle : undefined}
          >
            {resolvedVariant === "warning" ? (
              <HorizontalStepperWarningIconWrap>
                <WarningAlertIcon />
              </HorizontalStepperWarningIconWrap>
            ) : (
              <HorizontalStepperAnnouncementIcon size={sizeConfig[resolvedSize].announcementIcon} weight="regular" />
            )}
            <HorizontalStepperAnnouncementText>{announcement}</HorizontalStepperAnnouncementText>
          </HorizontalStepperAnnouncement>
        )}

        {canClose && (
          <HorizontalStepperCloseButton
            aria-label={closeLabel}
            icon={<X size={sizeConfig[resolvedSize].closeIcon} />}
            size={sizeConfig[resolvedSize].closeSize}
            variant="ghost"
            ownerState={{ size: resolvedSize }}
            onClick={onClose}
          />
        )}

        {resolvedSteps.length > 0 && (
          <HorizontalStepperList
            ownerState={{ size: resolvedSize, hasAnnouncement: Boolean(announcement) }}
            style={stepGridStyle}
            aria-live="polite"
          >
            {resolvedSteps.map((step, index) => {
              const isInteractive = Boolean(onStepSelect) && !step.disabled;
              const isMuted = step.state === "pending" || step.state === "disabled";
              const stepKey = step.id ?? index;
              const marker = <StepMarker state={step.state} size={resolvedSize} variant={resolvedVariant} />;
              const stepSecondaryText = step.secondaryText ?? step.description;
              const stepText = (
                <HorizontalStepperTextBlock ownerState={{ size: resolvedSize }}>
                  <HorizontalStepperStepLabel
                    ownerState={{ size: resolvedSize, variant: resolvedVariant, muted: isMuted }}
                    style={!isMuted && resolvedVariant === "warning" ? warningTextStyle : undefined}
                  >
                    {step.label}
                  </HorizontalStepperStepLabel>
                  {stepSecondaryText && (
                    <HorizontalStepperStepDescription ownerState={{ size: resolvedSize }}>
                      {stepSecondaryText}
                    </HorizontalStepperStepDescription>
                  )}
                </HorizontalStepperTextBlock>
              );

              return (
                <HorizontalStepperItem key={stepKey} aria-current={step.state === "active" ? "step" : undefined}>
                  {index < resolvedSteps.length - 1 && (
                    <HorizontalStepperConnector ownerState={{ size: resolvedSize }} aria-hidden="true" />
                  )}
                  {isInteractive ? (
                    <HorizontalStepperStepButton
                      type="button"
                      ownerState={{ size: resolvedSize }}
                      onClick={() => onStepSelect?.(step, index)}
                    >
                      {marker}
                      {stepText}
                    </HorizontalStepperStepButton>
                  ) : (
                    <HorizontalStepperStepStatic ownerState={{ size: resolvedSize }}>
                      {marker}
                      {stepText}
                    </HorizontalStepperStepStatic>
                  )}
                </HorizontalStepperItem>
              );
            })}
          </HorizontalStepperList>
        )}

        {(title || resolvedSubheading) && (
          <HorizontalStepperContent ownerState={{ size: resolvedSize, withoutSteps: resolvedSteps.length === 0 }}>
            {title && <HorizontalStepperTitle ownerState={{ size: resolvedSize }}>{title}</HorizontalStepperTitle>}
            {resolvedSubheading && (
              <HorizontalStepperSubheading ownerState={{ size: resolvedSize }}>{resolvedSubheading}</HorizontalStepperSubheading>
            )}
          </HorizontalStepperContent>
        )}
      </HorizontalStepperRoot>
    );
  }
);

HorizontalStepper.displayName = "HorizontalStepper";
